/**
 * Climate Readiness Score Panel (FRS-Lite)
 *
 * Principles:
 * - Never looks like a rating agency score (no S&P/Moody's style)
 * - Full transparency: "Learn how this is calculated" shows everything
 * - One-line positioning: not a certification, a decision-support metric
 * - Components shown with weights so there is no black box
 */
import React, { useState } from "react";
import { Info, X, BarChart2, Database, Zap, Layers, TrendingDown } from "lucide-react";

const BAND_CONFIG = {
  A: { color: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-200", bar: "bg-emerald-500" },
  B: { color: "text-teal-700",    bg: "bg-teal-50",     border: "border-teal-200",    bar: "bg-teal-500"   },
  C: { color: "text-amber-700",   bg: "bg-amber-50",    border: "border-amber-200",   bar: "bg-amber-500"  },
  D: { color: "text-red-700",     bg: "bg-red-50",      border: "border-red-200",     bar: "bg-red-400"    },
};

// Band guide — no financial claims in labels
const BAND_GUIDE = [
  ["A", "≥75",  "Advanced Readiness",      "bg-emerald-100 text-emerald-700"],
  ["B", "55–74","High Readiness Potential", "bg-teal-100 text-teal-700"],
  ["C", "35–54","Developing",               "bg-amber-100 text-amber-700"],
  ["D", "<35",  "Early Stage",              "bg-red-100 text-red-700"],
];

const COMPONENT_ICONS = {
  data_quality:          Database,
  financial_potential:   TrendingDown,
  implementation_ease:   Zap,
  scope_completeness:    Layers,
};

const COMPONENT_DESCRIPTIONS = {
  data_quality:        "How complete and reliable is the underlying data? Derived from the V1 engine's 5-component confidence score (factor reliability, evidence coverage, source quality).",
  financial_potential: "What is the financial return of identified decarbonisation actions? Based on estimated annual savings and ROI from prioritised action recommendations.",
  implementation_ease: "How quickly can the leading action be implemented? Based on the best payback period across identified opportunities (shorter payback = higher score).",
  scope_completeness:  "How much of the organisation's emissions footprint is captured? Scope 1+2 provides the base; each additional Scope 3 category strengthens the signal.",
};

const POSITIONING_STATEMENT =
  "This score is a decision-support metric derived from auditable emissions data. " +
  "It is not a certification, external rating, or carbon credit eligibility indicator. " +
  "All components are derived from V1 engine calculations with full factor provenance.";

export default function CRSPanel({ carsData, peerBenchmark, size = "default", showModal = true }) {
  const [open, setOpen] = useState(false);

  if (!carsData?.score) return null;

  const score     = Math.round(carsData.score);
  const band      = carsData.band  || "C";
  const label     = carsData.label || "Developing";
  const comp      = carsData.components || {};
  const bc        = BAND_CONFIG[band] || BAND_CONFIG.C;
  const isFRSLite = (carsData.source || "").includes("FRS-Lite");
  const scoreName = "Climate Readiness Score";
  const scoreTag  = isFRSLite ? "FRS-Lite" : "Internal";
  const peer      = peerBenchmark;
  const projected = carsData.projected_range;

  // ── Compact inline display ─────────────────────────────────────────────
  if (size === "compact") {
    return (
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${bc.bg} ${bc.border}`}
        data-testid="crs-compact">
        <span className={`text-xs font-bold tabular-nums ${bc.color}`}>{score}</span>
        <span className={`text-[9px] font-semibold ${bc.color}`}>/ 100</span>
        <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${bc.bg} ${bc.color}`}>Band {band}</span>
        {showModal && (
          <button onClick={() => setOpen(true)}
            className={`${bc.color} opacity-70 hover:opacity-100 transition-opacity`}
            data-testid="crs-info-btn">
            <Info size={11} />
          </button>
        )}
        {open && <CRSModal score={score} band={band} label={label} comp={comp} bc={bc} isFRSLite={isFRSLite} hints={carsData.improvement_hints || []} onClose={() => setOpen(false)} />}
      </div>
    );
  }

  // ── Default card display ───────────────────────────────────────────────
  return (
    <>
      <div className={`rounded-xl border p-4 ${bc.bg} ${bc.border}`} data-testid="crs-panel">
        {/* Header row */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-1.5">
              <BarChart2 size={13} className={bc.color} />
              <p className={`text-[10px] font-bold uppercase tracking-widest ${bc.color}`}>
                {scoreName}
              </p>
              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${bc.bg} ${bc.border} ${bc.color}`}>
                {scoreTag}
              </span>
            </div>
            <p className="text-[9px] text-slate-500 mt-0.5">{POSITIONING_STATEMENT.substring(0, 80)}...</p>
          </div>
          {showModal && (
            <button onClick={() => setOpen(true)}
              className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0 ml-2"
              data-testid="crs-info-btn">
              <Info size={14} />
            </button>
          )}
        </div>

        {/* Score display */}
        <div className="flex items-end gap-2 mb-3">
          <span className={`text-3xl font-bold tabular-nums ${bc.color}`}>{score}</span>
          <span className={`text-sm text-slate-400 mb-0.5`}>/ 100</span>
          <span className={`text-sm font-bold px-2 py-0.5 rounded-lg ml-1 ${bc.bg} ${bc.color} border ${bc.border} mb-0.5`}>
            Band {band} — {label}
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-white/60 rounded-full overflow-hidden mb-3">
          <div className={`h-full rounded-full transition-all duration-700 ${bc.bar}`}
            style={{ width: `${score}%` }} />
        </div>

        {/* Component mini-bars */}
        {Object.keys(comp).length > 0 && (
          <div className="space-y-1.5">
            {Object.entries(comp).map(([k, v]) => {
              if (typeof v !== "object") return null;
              const pct = Math.round((v.score / v.max) * 100);
              return (
                <div key={k} className="flex items-center gap-2">
                  <span className="text-[9px] text-slate-500 w-24 flex-shrink-0 truncate">{v.label || k}</span>
                  <div className="flex-1 h-1 bg-white/60 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${bc.bar} opacity-70`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[9px] font-mono text-slate-400 w-10 text-right">
                    {Math.round(v.score)}/{v.max}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Peer percentile — with maturity-adjusted honesty */}
        {peer?.available ? (
          <div className="flex items-start gap-1.5 bg-white/60 rounded-lg px-2.5 py-1.5 border border-slate-200">
            <div className="flex-1">
              <p className="text-[9px] text-slate-600 leading-snug">{peer.headline}</p>
              {peer.maturity?.confidence !== "high" && (
                <p className="text-[8px] text-amber-600 italic mt-0.5">
                  {peer.maturity?.note || "Interpret with caution — growing dataset."}
                </p>
              )}
            </div>
            {peer.maturity?.level && (
              <span className={`text-[7px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${
                peer.maturity.confidence === "high"   ? "bg-emerald-100 text-emerald-700" :
                peer.maturity.confidence === "medium" ? "bg-teal-100 text-teal-700" :
                "bg-amber-100 text-amber-700"
              }`}>{peer.maturity.level}</span>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <p className="text-[9px] text-slate-500 italic">
              Benchmarking improves as more companies participate
              {peer?.total_n > 0 && ` (${peer.total_n} submitted so far)`}.
            </p>
            {/* Subtle progress indication — no artificial threshold */}
            {peer?.total_n > 0 && (
              <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-slate-300 rounded-full transition-all"
                  style={{ width: `${Math.min(peer.total_n * 2, 100)}%` }} />
              </div>
            )}
          </div>
        )}

        {/* Projected range — only show when not complete */}
        {projected && score < 80 && (
          <div className="bg-white/60 rounded-lg px-2.5 py-1.5 border border-slate-200">
            <p className="text-[9px] text-slate-500">
              <span className="font-semibold text-slate-600">Potential: {projected.low}–{projected.high}/100 </span>
              with data upgrades.
            </p>
          </div>
        )}

        {showModal && (
          <button onClick={() => setOpen(true)}
            className={`text-[9px] font-semibold ${bc.color} hover:underline flex items-center gap-1`}
            data-testid="crs-learn-more-btn">
            <Info size={9} /> How is this calculated?
          </button>
        )}
      </div>

      {open && <CRSModal score={score} band={band} label={label} comp={comp} bc={bc} isFRSLite={isFRSLite} hints={carsData.improvement_hints || []} onClose={() => setOpen(false)} />}
    </>
  );
}

// ── Modal with full transparency ──────────────────────────────────────────
function CRSModal({ score, band, label, comp, bc, isFRSLite, hints, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        data-testid="crs-modal">
        {/* Header */}
        <div className={`sticky top-0 flex items-start justify-between px-5 py-4 border-b border-slate-100 bg-white rounded-t-2xl z-10`}>
          <div>
            <div className="flex items-center gap-2">
              <BarChart2 size={15} className={bc.color} />
              <h3 className="text-sm font-bold text-[#0F172A]">Climate Readiness Score</h3>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${bc.bg} ${bc.border} ${bc.color}`}>
                {isFRSLite ? "FRS-Lite" : "Internal"}
              </span>
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5">Full methodology transparency</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Score */}
          <div className={`rounded-xl p-4 ${bc.bg} border ${bc.border}`}>
            <div className="flex items-end gap-2">
              <span className={`text-4xl font-bold tabular-nums ${bc.color}`}>{score}</span>
              <span className="text-sm text-slate-400 mb-1">/ 100</span>
              <span className={`text-sm font-bold px-2 py-0.5 rounded ml-1 ${bc.color} bg-white/60 border ${bc.border} mb-1`}>
                Band {band} — {label}
              </span>
            </div>
            <div className="h-2 bg-white/60 rounded-full overflow-hidden mt-2">
              <div className={`h-full rounded-full ${bc.bar}`} style={{ width: `${score}%` }} />
            </div>
          </div>

          {/* Band guide — no financial claims */}
          <div>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wide mb-2">Band Reference Guide</p>
            <div className="grid grid-cols-4 gap-1.5">
              {BAND_GUIDE.map(([b, range, lbl, cls]) => (
                <div key={b} className={`rounded-lg p-2 text-center ${cls} ${b === band ? "ring-2 ring-current ring-offset-1" : "opacity-60"}`}>
                  <p className="text-sm font-bold">{b}</p>
                  <p className="text-[8px] font-semibold">{range}</p>
                  <p className="text-[8px] mt-0.5">{lbl}</p>
                </div>
              ))}
            </div>
            <p className="text-[8px] text-slate-400 italic mt-1">
              Band labels indicate relative readiness, not financial eligibility or creditworthiness.
            </p>
          </div>

          {/* Component breakdown */}
          {Object.keys(comp).length > 0 && (
            <div>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wide mb-2">Score Components</p>
              <div className="space-y-3">
                {Object.entries(comp).map(([k, v]) => {
                  if (typeof v !== "object") return null;
                  const pct = Math.round((v.score / v.max) * 100);
                  const Icon = COMPONENT_ICONS[k] || BarChart2;
                  return (
                    <div key={k} className="border border-slate-100 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <Icon size={12} className="text-slate-500" />
                          <p className="text-xs font-bold text-[#0F172A]">{v.label || k}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-bold tabular-nums text-[#0F172A]">{Math.round(v.score)}</span>
                          <span className="text-[9px] text-slate-400">/ {v.max} pts ({v.max}% weight)</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-1.5">
                        <div className={`h-full rounded-full ${bc.bar}`} style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-[9px] text-slate-500 leading-snug">
                        {COMPONENT_DESCRIPTIONS[k] || "Component of the Climate Readiness Score."}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Score → Action Loop */}
          {hints.length > 0 && (
            <div>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wide mb-2">How to Improve Your Score</p>
              <div className="space-y-2.5">
                {hints.map((h, i) => (
                  <div key={i} className="border border-teal-100 bg-teal-50/40 rounded-xl p-3">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5">
                        {(() => { const Ic = COMPONENT_ICONS[h.component] || BarChart2; return <Ic size={11} className="text-teal-600 flex-shrink-0" />; })()}
                        <p className="text-[10px] font-bold text-[#0F172A]">{h.action}</p>
                      </div>
                      <span className="text-[9px] font-bold text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap">
                        {h.impact}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full border ${
                        h.difficulty === "low"
                          ? "text-emerald-600 bg-emerald-50 border-emerald-200"
                          : "text-amber-600 bg-amber-50 border-amber-200"
                      }`}>{h.difficulty === "low" ? "Quick win" : "Medium effort"}</span>
                      <span className="text-[8px] text-slate-400">→ Go to {h.journey_step} step</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data sources */}
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wide mb-1.5">Data Sources</p>
            <div className="space-y-1">
              {[
                ["Emission factors", "V1 engine — DeCarbonIQ 97-factor library (GHG Protocol / IPCC 2006)"],
                ["Calculation", "ActivityRecord-based with reproducibility hash and factor provenance"],
                ["Confidence scoring", "5-component: completeness, evidence, factor reliability, source quality, benchmark fit"],
                ["Action data", "Geography-aware action recommendations (Africa-calibrated capex ranges)"],
              ].map(([l, d]) => (
                <div key={l} className="flex items-start gap-1.5">
                  <span className="text-teal-500 text-[9px] font-bold flex-shrink-0 mt-0.5">•</span>
                  <div>
                    <span className="text-[9px] font-bold text-slate-600">{l}: </span>
                    <span className="text-[9px] text-slate-500">{d}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Positioning statement */}
          <div className="border border-amber-200 bg-amber-50 rounded-xl p-3">
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide mb-1">Important</p>
            <p className="text-[10px] text-amber-700 leading-snug">{POSITIONING_STATEMENT}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
